import { useEffect, useRef } from 'react'

import { usePathname, useRouter } from 'next/navigation'

import { rejectStickyView } from '@/components/AppShell/stickyNetworkSelection'
import { type ViewVariable } from '@/components/pages/Digraph'
import { ShownVariables, useGlobalState } from '@/global-state/useGlobalState'
import { getTrpcErrorCode } from '@/services/queryClient'
import { showError, showWarning } from '@/utils/toast'
import { trpc } from '@/utils/trpc'

export type UseViewProps = {
  networkId: string | undefined
  viewId: string | undefined
}

export const useRefetchView = () => {
  const utils = trpc.useUtils()
  return async (viewId: string | undefined, networkId: string) => {
    await utils.view.getViewWithPositions.refetch({
      viewId: viewId as string,
      networkId,
    })
  }
}

/** Sends the user to the view-less variant of the route they are already on,
 * so recovering from a bad view id on `/list/<network>/<view>` doesn't also
 * move them to the digraph. */
const redirectToNetwork = (
  router: ReturnType<typeof useRouter>,
  pathname: string | null,
  networkId: string | undefined,
): void => {
  // The query is gated on a defined networkId, so this is defensive only —
  // but `/undefined` would land on a 404 rather than anywhere recoverable.
  if (!networkId) {
    router.push('/')
    return
  }
  const prefix = pathname?.startsWith('/list/') ? '/list' : ''
  router.push(`${prefix}/${networkId}`)
}

export const useView = ({ networkId, viewId }: UseViewProps) => {
  const hideVariables = useGlobalState((state) => state.hideVariables)
  const handleViewUpdate = useGlobalState((state) => state.handleViewUpdate)
  const isCreatingLayout = useGlobalState((state) => state.isCreatingLayout)
  const utils = trpc.useUtils()
  const router = useRouter()
  // The NOT_FOUND check below reads the code via `getTrpcErrorCode`, not
  // `error instanceof TRPCError`. A client-side query error is a
  // `TRPCClientError` and is never an instance of `@trpc/server`'s `TRPCError`,
  // so that check was always false: the redirect was dead code and a deleted
  // view fell through to the misleading "check your internet connection" toast
  // instead. `view.spec.ts:94` failed on `toHaveURL` with 19 polls showing the
  // view id still in the URL (KBE-770).
  //
  // There used to be a `!isPending` term here as well, fed by two
  // `trpc.view.*.useMutation()` instances created purely to read `isPending`.
  // React Query reports per-observer state and nothing was ever fired through
  // those instances, so the term was `true` unconditionally and gated nothing
  // (KBE-770). Removed rather than repaired: a real gate suppresses the
  // NOT_FOUND branch below, and a view deleted while a membership write is in
  // flight then never redirects. The stale-response race it was reaching for is
  // handled at the source instead — `VIEW_MEMBERSHIP_MUTATION_SCOPE` keeps the
  // delete/upsert pair in order, so the response can no longer disagree with
  // what actually landed.
  const { data: view, error } = trpc.view.getViewWithPositions.useQuery(
    { viewId: viewId as string, networkId: networkId as string },
    {
      enabled: !!viewId && !isCreatingLayout && !!networkId,
      // The effect below handles both branches, so the global
      // `queryCache.onError` toast would only duplicate them — and on the
      // `NOT_FOUND` branch it actively contradicts: the user is redirected to a
      // working page and left with a never-fading "Failed to load data" toast
      // describing a view that is simply gone. That branch only became
      // reachable once `getTrpcErrorCode` replaced the always-false
      // `instanceof TRPCError` check, so the toast came with it (KBE-770).
      meta: {
        onError: (error: Error) =>
          console.error('Loading the view failed', error),
      },
    },
  )
  const pathname = usePathname()
  useEffect(() => {
    if (!error) return
    const code = getTrpcErrorCode(error)
    // Both codes mean this view can never load under this network again —
    // NOT_FOUND because it is gone, BAD_REQUEST because it belongs to a
    // different network (a shared or hand-edited URL pairing two ids that
    // never went together, KBE-718). Either way the pair is rejected from
    // sticky state before redirecting, or the dead id comes straight back on
    // the next route that falls back to it (`/list/<network>` does).
    //
    // On the mismatch we drop the view rather than following it to its own
    // network: the network id is the more deliberate half of such a link, so
    // silently moving the user to another network is the more surprising
    // outcome.
    if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') {
      if (networkId && viewId) rejectStickyView(networkId, viewId)
      if (code === 'NOT_FOUND') {
        showWarning('View has been deleted.', {
          description: 'Redirecting to the network...',
        })
      } else {
        showWarning('That view belongs to a different network.', {
          description: 'Redirecting to the network so you can pick a view...',
        })
      }
      redirectToNetwork(router, pathname, networkId)
      return
    }
    showError('Failed to load view.', {
      description: 'Check your internet connection and try again.',
    })
  }, [error, networkId, pathname, router, viewId])

  // Returning to a digraph view from another route (the merge/diff view,
  // history, diagnosis, …) remounts this hook, but the view query is served
  // from the cache (`staleTime: Infinity`) and can hand back the same `view`
  // object it held before — so the populate effect below runs but re-applies
  // stale positions, and any transient flag that emptied `shownVariables` on
  // the way out is never corrected. That left the canvas looking broken until
  // the user manually re-selected the same view, which forces a refetch.
  // Do that refetch automatically, once per view entry, so back-navigation
  // recovers on its own (KBE-874). Skipped while a layout is being created
  // (the enabled query is intentionally paused then, and the overview owns the
  // canvas). The refetch yields a fresh `view` object, which re-fires the
  // populate effect with up-to-date positions.
  const reloadedViewKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!networkId || !viewId || isCreatingLayout) {
      reloadedViewKeyRef.current = undefined
      return
    }
    const key = `${networkId}:${viewId}`
    if (reloadedViewKeyRef.current === key) return
    reloadedViewKeyRef.current = key
    // Only a re-entry needs the forced refetch: on first load the cache is empty
    // and the enabled query fetches it. Refetching only when data is already
    // cached avoids a redundant double fetch on the initial page load.
    const cached = utils.view.getViewWithPositions.getData({
      viewId,
      networkId,
    })
    if (cached !== undefined) {
      void utils.view.getViewWithPositions.refetch({ viewId, networkId })
    }
  }, [networkId, viewId, isCreatingLayout, utils])

  const appliedViewIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (view !== undefined) {
      const newShownVariables = convertVariablePositionListToShownVariables(
        view.positions,
      )
      hideVariables((id) => !newShownVariables.has(id))
      // The first application of a view to this page instance — a fresh mount
      // (returning from another route) or a switch to a different view — must
      // go through unconditionally: `handleViewUpdate`'s drag-skip guards a
      // background refresh of the view already on screen, not the initial load
      // (KBE-874).
      const isInitialApplication = appliedViewIdRef.current !== view.id
      appliedViewIdRef.current = view.id
      handleViewUpdate(newShownVariables, view.layout, view.id, {
        force: isInitialApplication,
      })
    }
  }, [handleViewUpdate, hideVariables, view])

  return { view }
}

const convertVariablePositionListToShownVariables = (
  viewVariables: ViewVariable[],
): ShownVariables =>
  new Map(
    viewVariables.map(({ variableId, x, y }) => [
      variableId,
      {
        center: { x, y },
      },
    ]),
  )

export default useView
